import React, { useEffect, useState } from "react";
import { DeletePOIReport, GetPOIReportByGuid } from "../../../services/OiService";
import OiPageHeader, { OiHeaderButton } from "../OiPageHeader";
import { generatePrintHTML } from "./OiPrintReport";
import { useToast } from "../../ToastProvider";
import { fadeIn } from "./OiForm";
import { FileReference } from "../OiReport";
import { useEventReport } from "../redux/ReduxContext";
import { useParams, useNavigate } from "react-router-dom";
import { EventReport } from "../redux/eventReport";
import { email } from "../../../App";
import { DocumentBulletList20Filled } from "@fluentui/react-icons";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger } from "@fluentui/react-components";


interface OiReadReportProps {
    onOpenFileGallery?: (files: FileReference[], initialIndex: number) => void;
}

export default function OiReadReport({ onOpenFileGallery }: OiReadReportProps) {
    const { showToast } = useToast();
    const navigate = useNavigate();
    const { uuid } = useParams<{ uuid: string }>();
    const { state, dispatch } = useEventReport();

    useEffect(() => {

        if (!uuid) return;

        GetPOIReportByGuid(uuid).then(report => {
            if (report) {
                const data: EventReport = report;
                dispatch({ type: 'HYDRATE', payload: data });
                console.log("Loaded report data:", data);
            }
        });

    }, [uuid, email]);


    const formatDateTime = (dateTimeString: string) => {
        if (!dateTimeString) return "Not set";
        if (dateTimeString.includes(' ')) {
            dateTimeString = dateTimeString.replace(' ', 'T');
        }
        if (!dateTimeString.endsWith('Z')) {
            dateTimeString += 'Z';
        }
        try {
            const date = new Date(dateTimeString);
            return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        } catch {
            console.error('Invalid date string:', dateTimeString);
            return dateTimeString;
        }
    };
    const onEdit = () => {
        navigate(`/Oi/editEventReport/${uuid}`);
    }
    const [open, setOpen] = useState(false);
    const DeleteReportDialog = () => {
        const handleConfirmDelete = async () => {

            try {
                if (uuid) {
                    const success = await DeletePOIReport(uuid);
                    if (success) {
                        showToast({ title: 'Deleted!', intent: 'success', body: 'Report removed.' });
                        navigate('/Oi');

                    } else {
                        showToast({ title: 'Failed', intent: 'error', body: 'Try again.' });
                    }
                }

            } catch (error) {
                console.error(error);
                showToast({ title: 'Error', intent: 'error', body: 'Error deleting report.' });
            } finally {

                setOpen(false);
            }
        };

        return (
            <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
                <DialogTrigger>
                    <OiHeaderButton variant="danger">Delete</OiHeaderButton>
                </DialogTrigger>

                <DialogSurface className="">
                    <DialogBody className="h-full flex flex-col">
                        <DialogTitle>Delete Report</DialogTitle>
                        <DialogContent className="flex-1 overflow-hidden">
                            Are you sure you want to delete "{state.testName}"?
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="secondary" onClick={() => setOpen(false)} >
                                Cancel
                            </Button>
                            <Button appearance="primary" onClick={handleConfirmDelete}>
                                Delete
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        );
    };



    const handlePrint = () => {
        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'absolute';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = 'none';
        document.body.appendChild(printFrame);

        const doc = printFrame.contentWindow?.document;
        if (!doc) return;

        doc.open();
        doc.write(generatePrintHTML(state));
        doc.close();

        printFrame.onload = () => {
            printFrame.contentWindow?.focus();
            printFrame.contentWindow?.print();
            document.body.removeChild(printFrame);
        };
    };

    const textYellowBlue = "text-oi-button-blue dark:text-oi-powder-form-yellow"
    const textGrayDark = "text-primary-dark dark:text-primary-light";
    const box = "bg-white dark:bg-oi-dark-background p-3 drop-shadow-md";

    const InfoCard = ({ title, children, className = "" }: { title: string; children: React.ReactNode; className?: string }) => (
        <div className={`p-3 ${className} ${box} ${textGrayDark}`}>
            <h3 className="text-primary-dark dark:text-primary-light font-bold mb-2 text-sm">{title}:</h3>
            <div className={`${textYellowBlue} font-medium text-sm`}>
                {children}
            </div>
        </div>
    );



    return (
        <>
            <style>{fadeIn}</style>
            <div className={`w-full max-w-none mx-6 pb-4 testSelection ${textGrayDark}`} style={{ animation: "fadeIn 1s forwards" }}>
                <OiPageHeader
                    icon={<DocumentBulletList20Filled />}
                    title="Event report"
                    id={state.testName}
                    createdAt={state.createdAt}
                    actions={<>
                        <DeleteReportDialog />
                        <OiHeaderButton onClick={onEdit}>Edit</OiHeaderButton>
                        <OiHeaderButton onClick={() => navigate(`/Oi/eventReportClone/${uuid}`)}>Clone</OiHeaderButton>
                        <OiHeaderButton onClick={handlePrint}>Print</OiHeaderButton>
                    </>}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                    <InfoCard title="Test Instrument">
                        {state.testSelection || "Ultra (103537)"}
                    </InfoCard>

                    <InfoCard title="Test id">
                        {state.testName || "Not specified"}
                    </InfoCard>

                    <InfoCard title="Test facility">
                        {state.elementName || "Not specified"}
                    </InfoCard>

                    <InfoCard title="Author">
                        {state.author || "Not specified"}
                    </InfoCard>

                    <InfoCard title="Test start date hours">
                        {state.startTime ? formatDateTime(state.startTime) : ""}
                    </InfoCard>

                    <InfoCard title="Test finish date hours">
                        {state.endTime ? formatDateTime(state.endTime) : ""}
                    </InfoCard>
                </div>

                <div className="mt-3">
                    <InfoCard title="Test comment" className="w-full">
                        <div className="text-yellow-400 leading-relaxed">
                            {state.comment || "No comment"}
                        </div>
                    </InfoCard>
                </div>

                <div className="mt-3">
                    <InfoCard title="Manufacturing machines">
                        {state.taggedElements.length > 0 ? state.taggedElements.join(", ") : "Not specified"}
                    </InfoCard>
                </div>

                <div className="mt-3">
                    <InfoCard title="Manufacturing comment" className="w-full">
                        <div className="text-yellow-400 leading-relaxed">
                            {state.taggedElementsComments.length > 0 ? state.taggedElementsComments.join(", ") : "No manufacturing comment"}
                        </div>
                    </InfoCard>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <InfoCard title="Tagged Elements Start Time">
                        {state.taggedElementsStart.length > 0 ? formatDateTime(state.taggedElementsStart[0]) : "Not set"}
                    </InfoCard>

                    <InfoCard title="Tagged Elements End Time">
                        {state.taggedElementsEnd.length > 0 ? formatDateTime(state.taggedElementsEnd[0]) : "Not set"}
                    </InfoCard>
                </div>

                <div className="grid grid-cols-1 gap-3 mt-3">
                    <InfoCard title="Tags">
                        {state.tags.length > 0 ? state.tags.join(", ") : "Not specified"}
                    </InfoCard>

                    <InfoCard title="DMC">
                        {state.dataMatrixCodes.length > 0 ? state.dataMatrixCodes.join(", ") : "Not specified"}
                    </InfoCard>

                    <InfoCard title="Tagged staff">
                        {state.taggedUsers.length > 0 ? state.taggedUsers.join(", ") : "Not specified"}
                    </InfoCard>

                    <InfoCard title="Attachments">
                        {state.referencedDocuments.length > 0 ? (
                            <div className="space-y-1">
                                {state.referencedDocuments.map((file, index) => {
                                    return (
                                        <div key={index}>

                                            <button
                                                onClick={() => {
                                                    try {
                                                        onOpenFileGallery?.(state.referencedDocuments, index);
                                                    } catch (error) {
                                                        console.error('Error opening file:', error);
                                                        showToast({ title: 'Error opening file', intent: 'error', body: `Could not open file: ${file}` });
                                                    }
                                                }}
                                                className="text-yellow-400 hover:text-yellow-300 underline bg-transparent border-none cursor-pointer p-0 text-left text-sm"
                                                title={`Open ${file.fileName}`}
                                            >
                                                {file.fileName}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <span className="text-yellow-400 text-sm">No attachments</span>
                        )}
                    </InfoCard>
                </div>
            </div>
        </>
    );
}
